import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Trash2 } from 'lucide-react';
import MinimalisticTable, { ColumnDef } from '@/shared/components/ui/MinimalisticTable';
import AppDialog from '@/shared/components/ui/AppDialog';
import { timeAgo } from '@/utils/date';
import type { Worker } from '../../api/workersApi';
import { fetchHandlersV2Api, type HandlerSpecRow } from '../../api/handlersV2Api';
import { fetchWorkflowsApi, triggerWorkflowApi } from '../../api/workflowsApi';
import { buildPresetFromHandler, formatDate } from './createPresetV2';
import type { CreateHandlerPreset } from './CreateHandlerFormV2';

type WorkersByKeyMap = Record<string, Worker>;

const HANDLER_QUERY_KEY = ['admin', 'orchestrator', 'handlers'] as const;

interface Props {
  workers: Worker[];
  workersLoading: boolean;
  workersError: Error | null;
  onNew: () => void;
  onNewVersion: (preset: CreateHandlerPreset) => void;
}

export default function HandlerCatalogMenuPaneV2({ workers, workersLoading, workersError, onNew, onNewVersion }: Props) {
  const qc = useQueryClient();
  const [tick, setTick] = React.useState(0);

  const [deleteOpen, setDeleteOpen] = React.useState(false);
  const [deleteHandlerKey, setDeleteHandlerKey] = React.useState('');
  const [deleteError, setDeleteError] = React.useState<string | null>(null);

  // Keep the “Updated” column dynamic (e.g. 2 min ago → 3 min ago).
  React.useEffect(() => {
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, []);

  const workersSorted = React.useMemo(() => {
    return workers.slice().sort((a, b) => String(a.worker_key).localeCompare(String(b.worker_key)));
  }, [workers]);

  const workerByKey = React.useMemo<WorkersByKeyMap>(() => {
    return workersSorted.reduce((acc, worker) => {
      const key = String(worker.worker_key ?? '').trim();
      if (key) acc[key] = worker;
      return acc;
    }, {} as WorkersByKeyMap);
  }, [workersSorted]);

  const { data, isLoading, error } = useQuery<{ items: HandlerSpecRow[] }, Error>({
    queryKey: [...HANDLER_QUERY_KEY, 'v2'],
    queryFn : async () => {
      const res = await fetchHandlersV2Api({ limit: 500 });
      return { items: res.items ?? [] };
    },
    // Keep server-driven fields (like updated_at) fresh without user interaction.
    refetchInterval: 30_000,
    refetchIntervalInBackground: true,
  });

  const handlers = data?.items ?? [];

  const handlersSorted = React.useMemo(() => {
    const items = handlers.slice();
    items.sort((a, b) => String(a.handler_key).toLocaleLowerCase().localeCompare(String(b.handler_key).toLocaleLowerCase()));
    return items;
  }, [handlers]);

  const deleteWorkflowMutation = useMutation({
    mutationFn: async (handler_key: string) => {
      // Resolve a concrete workflow catalog version to trigger.
      // (Trigger endpoint supports omitting version, but this keeps behavior deterministic.)
      const rows = await fetchWorkflowsApi({ workflow_key: 'system.handler.delete', active: true });
      let max = 0;
      for (const w of rows ?? []) {
        if (w?.workflow_key !== 'system.handler.delete') continue;
        if (typeof w.version === 'number' && Number.isFinite(w.version) && w.version > max) max = w.version;
      }

      return triggerWorkflowApi({
        workflow_key: 'system.handler.delete',
        version: max > 0 ? max : undefined,
        inputs: { handler_key },
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [...HANDLER_QUERY_KEY, 'v2'] });
      setDeleteOpen(false);
      setDeleteHandlerKey('');
      setDeleteError(null);
    },
    onError: (err: any) => {
      setDeleteError(err?.message || 'Failed to trigger system.handler.delete');
    },
  });

  const columns: ColumnDef<HandlerSpecRow>[] = React.useMemo(() => [
    { header: 'Handler', accessor: 'handler_key' },
    {
      header  : 'Worker',
      accessor: 'worker_key',
      Cell    : (value: string | undefined) => {
        const wk = String(value ?? '').trim();
        const worker = wk ? workerByKey[wk] : undefined;
        if (!worker) return wk || '—';
        return (
          <div className="flex flex-col text-xs">
            <span>{worker.worker_key}</span>
            <span className="text-neutral-400">{worker.transport}</span>
          </div>
        );
      },
    },
    {
      header  : 'Status',
      accessor: 'is_active',
      Cell    : (value: boolean | undefined) => (
        <span className={value ? 'text-emerald-400' : 'text-neutral-500'}>
          {value ? 'Active' : 'Disabled'}
        </span>
      ),
    },
    {
      header  : 'Updated',
      accessor: 'updated_at',
      Cell    : (value: string) => (
        <span title={formatDate(value)} className="text-neutral-300">
          {timeAgo(value)}
        </span>
      ),
    },
    {
      header: '',
      accessor: '',
      Cell: (_value: any, row: HandlerSpecRow) => {
        const hk = String(row.handler_key ?? '').trim();
        const disabled = !hk || deleteWorkflowMutation.isPending;
        return (
          <button
            type="button"
            title="Delete (trigger system.handler.delete workflow)"
            className="p-1 rounded text-neutral-500 hover:text-rose-400 disabled:opacity-60"
            disabled={disabled}
            onClick={(e) => {
              e.stopPropagation();
              if (!hk) return;
              setDeleteError(null);
              setDeleteHandlerKey(hk);
              setDeleteOpen(true);
            }}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        );
      },
    },
  ], [workerByKey, tick]);

  return (
    <div className="h-full min-h-0 flex flex-col gap-3">
      <div className="shrink-0">
        {workersError && (
          <p className="mt-2 text-xs text-amber-400">
            Workers failed to load: {workersError.message}
          </p>
        )}

        {isLoading && <p className="mt-2 text-sm text-neutral-400">Loading handlers…</p>}
        {error && (
          <p className="mt-2 text-sm text-rose-400">
            Failed to load handlers: {error.message}
          </p>
        )}
      </div>

      <div className="flex-1 min-h-0 overflow-auto pr-1">
        {!isLoading && !error && (
          <MinimalisticTable<HandlerSpecRow>
            columns={columns}
            data={handlersSorted}
            noDataMessage="No handler entries."
            showSearch
            searchPlaceholder="Search handlers…"
              onRowClick={(row) => {
                const preset = buildPresetFromHandler(row);
                const wk = String(row.worker_key ?? '').trim();
                if (wk) {
                  const worker = workerByKey[wk];
                  if (worker) preset.worker_id = String(worker.worker_id);
                }
                onNewVersion(preset);
              }}
          />
        )}
      </div>

      <AppDialog
        open={deleteOpen}
        onOpenChange={(v) => {
          if (!v) {
            setDeleteError(null);
            setDeleteHandlerKey('');
          }
          setDeleteOpen(v);
        }}
        title="Delete handler"
        description="Triggers the system.handler.delete workflow for the selected handler key."
        confirmTone="danger"
        confirmLabel={deleteWorkflowMutation.isPending ? 'Triggering…' : 'Trigger delete'}
        confirmDisabled={!deleteHandlerKey.trim()}
        isBusy={deleteWorkflowMutation.isPending}
        onConfirm={async () => {
          const hk = deleteHandlerKey.trim();
          if (!hk) return;
          setDeleteError(null);
          await deleteWorkflowMutation.mutateAsync(hk);
        }}
      >
        {deleteError && <div className="text-sm text-rose-400">{deleteError}</div>}

        <div className="space-y-2">
          <label className="text-xs uppercase tracking-wide text-neutral-400">handler_key</label>
          <input
            value={deleteHandlerKey}
            readOnly
            className="w-full bg-black border border-neutral-700 rounded px-2 py-2 text-sm outline-none"
          />
          <div className="text-xs text-neutral-500">This will permanently delete the handler registry row (hard delete).</div>
        </div>
      </AppDialog>

      {workersLoading && (
        <p className="text-xs text-neutral-500">Fetching workers to populate dropdown…</p>
      )}
    </div>
  );
}
